/**
 * Condition serialization for template editor
 * Handles wrapping, formatting, and template generation for conditions
 */

import {
  CONDITION_HIGHLIGHT_CLASS,
  CONDITION_OPERATORS,
  ConditionType,
  toQuotedRootPath,
} from '../constants';

const escapeAttr = (value: string): string => value.replace(/"/g, '&quot;');

// A leading "@." in the condition's variable path is quoted to "@". only
// when writing the data-start-if/data-end-if attribute - see toQuotedRootPath.
const escapeConditionAttr = (expr: string): string =>
  escapeAttr(toQuotedRootPath(expr));

/**
 * Build the raw condition expression (without handlebars wrapper)
 * The variable path is used exactly as given - no prefix rewriting.
 */
const buildConditionExpr = (
  variablePath: string,
  operator?: string,
  compareValue?: string | number | boolean | null
): string => {
  let expr = variablePath;

  if (operator && compareValue !== undefined) {
    let formattedValue: string;
    if (typeof compareValue === 'string') {
      formattedValue = `"${compareValue}"`;
    } else if (compareValue === null) {
      formattedValue = 'null';
    } else {
      formattedValue = String(compareValue);
    }
    expr += ` ${operator} ${formattedValue}`;
  }

  return expr;
};

/**
 * Wrap condition start marker
 */
export const wrapConditionStart = (conditionString: string): string => {
  const innerCondition = conditionString
    .replace(/^\{\{#(?:if|unless)\s+/, '')
    .replace(/\}\}$/, '');
  const escaped = escapeConditionAttr(innerCondition);
  return `<span class="${CONDITION_HIGHLIGHT_CLASS}" contenteditable="false" data-start-if="${escaped}">${conditionString}</span>`;
};

/**
 * Wrap else marker
 */
export const wrapElseMarker = (): string => {
  return `<span class="${CONDITION_HIGHLIGHT_CLASS}" contenteditable="false" data-else>{{else}}</span>`;
};

/**
 * Wrap condition end marker
 */
export const wrapConditionEnd = (condition: string): string => {
  const escaped = escapeConditionAttr(condition);
  return `<span class="${CONDITION_HIGHLIGHT_CLASS}" contenteditable="false" data-end-if="${escaped}">{{/if}}</span>`;
};

/**
 * Automatically wraps unwrapped condition patterns
 * Handles: {{#if path}}, {{#unless path}}, {{else}}, {{/if}}
 */
export const autoWrapConditions = (html: string): string => {
  const ifRegex =
    /(\{\{#(?:if|unless)\s+[^{}]+?\}\})([\s\S]*?)(\{\{else\}\})?([\s\S]*?)(\{\{\/if\}\})/g;

  const wrappedHtml = html.replace(
    ifRegex,
    (fullMatch, startMarker, ifContent, elseMarker, elseContent) => {
      if (fullMatch.includes(CONDITION_HIGHLIGHT_CLASS)) {
        return fullMatch;
      }

      const innerCondition = startMarker
        .replace(/^\{\{#(?:if|unless)\s+/, '')
        .replace(/\}\}$/, '');

      const wrappedStart = wrapConditionStart(startMarker);
      const wrappedEnd = wrapConditionEnd(innerCondition);

      if (elseMarker) {
        const wrappedElse = wrapElseMarker();
        const cleanIfContent = ifContent.trim();
        const cleanElseContent = elseContent.trim();

        return `${wrappedStart}\n${cleanIfContent}\n${wrappedElse}\n${cleanElseContent}\n${wrappedEnd}`;
      }

      const cleanContent = ifContent.trim();
      return `${wrappedStart}\n${cleanContent}\n${wrappedEnd}`;
    }
  );

  return wrappedHtml;
};

/**
 * Create condition start marker
 */
export const createConditionStart = (
  type: 'if' | 'unless',
  variablePath: string,
  operator?: string,
  compareValue?: string | number | boolean | null
): string => {
  const conditionExpr = buildConditionExpr(
    variablePath,
    operator,
    compareValue
  );
  const handlebarsCondition = `{{#${type} ${conditionExpr}}}`;
  const escaped = escapeConditionAttr(conditionExpr);
  return `<span class="${CONDITION_HIGHLIGHT_CLASS}" contenteditable="false" data-start-if="${escaped}">${handlebarsCondition}</span>`;
};

/**
 * Create condition end marker
 */
export const createConditionEnd = (conditionExpr: string): string => {
  const escaped = escapeConditionAttr(conditionExpr);
  return `<span class="${CONDITION_HIGHLIGHT_CLASS}" contenteditable="false" data-end-if="${escaped}">{{/if}}</span>`;
};

/**
 * Create else marker
 */
export const createElseMarker = (): string => {
  return `<span class="${CONDITION_HIGHLIGHT_CLASS}" contenteditable="false" data-else>{{else}}</span>`;
};

/**
 * Simple truthiness condition template
 */
export const simpleConditionTemplate = (variablePath: string): string => {
  const expr = buildConditionExpr(variablePath);
  return `${createConditionStart('if', variablePath)}
<p>Content shown when ${variablePath} is truthy</p>
${createConditionEnd(expr)}`;
};

/**
 * Simple condition with else
 */
export const simpleConditionWithElseTemplate = (
  variablePath: string
): string => {
  const expr = buildConditionExpr(variablePath);
  return `${createConditionStart('if', variablePath)}
<p>Content when ${variablePath} is truthy</p>
${createElseMarker()}
<p>Content when ${variablePath} is falsy</p>
${createConditionEnd(expr)}`;
};

/**
 * Negation condition (unless)
 */
export const negationConditionTemplate = (variablePath: string): string => {
  const expr = buildConditionExpr(variablePath);
  return `${createConditionStart('unless', variablePath)}
<p>Content shown when ${variablePath} is falsy</p>
${createConditionEnd(expr)}`;
};

/**
 * Equality comparison condition
 */
export const equalityConditionTemplate = (
  variablePath: string,
  compareValue: string | number | boolean | null,
  operator: string = CONDITION_OPERATORS.EQUALS
): string => {
  const expr = buildConditionExpr(variablePath, operator, compareValue);
  const operatorText =
    operator === CONDITION_OPERATORS.EQUALS ? 'equals' : 'does not equal';
  return `${createConditionStart('if', variablePath, operator, compareValue)}
<p>Content when ${variablePath} ${operatorText} ${compareValue}</p>
${createConditionEnd(expr)}`;
};

/**
 * Comparison condition (greater than, less than, etc.)
 */
export const comparisonConditionTemplate = (
  variablePath: string,
  operator: string,
  compareValue: number
): string => {
  const expr = buildConditionExpr(variablePath, operator, compareValue);
  return `${createConditionStart('if', variablePath, operator, compareValue)}
<p>Content when ${variablePath} ${operator} ${compareValue}</p>
${createConditionEnd(expr)}`;
};

/**
 * String comparison condition
 */
export const stringComparisonTemplate = (
  variablePath: string,
  operator: string,
  compareValue: string
): string => {
  const expr = buildConditionExpr(variablePath, operator, compareValue);
  return `${createConditionStart('if', variablePath, operator, compareValue)}
<p>Content when ${variablePath} ${operator} "${compareValue}"</p>
${createConditionEnd(expr)}`;
};

/**
 * Empty check condition
 */
export const emptyCheckTemplate = (
  variablePath: string,
  operator: string = CONDITION_OPERATORS.IS_EMPTY
): string => {
  const expr = buildConditionExpr(variablePath, operator, null);
  const message =
    operator === CONDITION_OPERATORS.IS_EMPTY ? 'is empty' : 'is not empty';
  return `${createConditionStart('if', variablePath, operator, null)}
<p>${variablePath} ${message}</p>
${createConditionEnd(expr)}`;
};

/**
 * Get condition template based on type and parameters
 */
export const getConditionTemplate = (
  conditionType: ConditionType,
  variablePath: string,
  options?: {
    operator?: string;
    compareValue?: string | number | boolean | null;
    hasElse?: boolean;
  }
): string => {
  switch (conditionType) {
    case ConditionType.Simple:
      return options?.hasElse
        ? simpleConditionWithElseTemplate(variablePath)
        : simpleConditionTemplate(variablePath);

    case ConditionType.Negation:
      return negationConditionTemplate(variablePath);

    case ConditionType.Comparison:
      if (!options?.operator || options?.compareValue === undefined) {
        return equalityConditionTemplate(variablePath, 'value');
      }

      // Empty check operators don't need a value
      if (
        options.operator === CONDITION_OPERATORS.IS_EMPTY ||
        options.operator === CONDITION_OPERATORS.IS_NOT_EMPTY
      ) {
        return emptyCheckTemplate(variablePath, options.operator);
      }

      // String comparison operators
      if (
        options.operator === CONDITION_OPERATORS.CONTAINS ||
        options.operator === CONDITION_OPERATORS.NOT_CONTAINS ||
        options.operator === CONDITION_OPERATORS.STARTS_WITH ||
        options.operator === CONDITION_OPERATORS.ENDS_WITH
      ) {
        return stringComparisonTemplate(
          variablePath,
          options.operator,
          String(options.compareValue)
        );
      }

      // Numeric comparison operators
      if (typeof options.compareValue === 'number') {
        return comparisonConditionTemplate(
          variablePath,
          options.operator,
          options.compareValue
        );
      }

      // Equality/inequality operators
      return equalityConditionTemplate(
        variablePath,
        options.compareValue!,
        options.operator
      );

    default:
      return simpleConditionTemplate(variablePath);
  }
};

/**
 * Condition format options for UI dropdown
 */
export const CONDITION_FORMAT_OPTIONS = [
  {
    key: ConditionType.Simple,
    label: 'Simple (if field exists)',
    description: 'Show content if field has a value',
  },
  {
    key: ConditionType.Negation,
    label: 'Negation (unless field exists)',
    description: 'Show content if field is empty or false',
  },
  {
    key: ConditionType.Comparison,
    label: 'Comparison (if field equals/greater/less)',
    description: 'Compare field value with another value',
  },
];

/**
 * Operator options for comparison conditions
 */
export const COMPARISON_OPERATOR_OPTIONS = [
  { value: CONDITION_OPERATORS.EQUALS, label: 'Equals (==)' },
  { value: CONDITION_OPERATORS.NOT_EQUALS, label: 'Not Equals (!=)' },
  { value: CONDITION_OPERATORS.GREATER_THAN, label: 'Greater Than (>)' },
  {
    value: CONDITION_OPERATORS.GREATER_THAN_OR_EQUAL,
    label: 'Greater or Equal (>=)',
  },
  { value: CONDITION_OPERATORS.LESS_THAN, label: 'Less Than (<)' },
  {
    value: CONDITION_OPERATORS.LESS_THAN_OR_EQUAL,
    label: 'Less or Equal (<=)',
  },
];
